// /docs — In-app API documentation page.
//
// Static content page documenting the SCIM provisioning API — the only
// endpoint Ring Enterprise customers interact with programmatically. Their
// IdP (e.g. Azure AD, Okta) pushes user and group changes to our SCIM v2
// endpoints; everything else (OIDC login, the BFF /api/* endpoints, session
// management, audit views) is internal to the browser UI and is not a
// customer-facing programmatic surface, so it is intentionally not documented
// here. Accessible to all authenticated roles (not just FLEET_ADMIN).
//
// All content is hardcoded — no API fetches.
//
// Audience: enterprise IT admins integrating their IdP.
// Tone: "you must" / "your IdP" (external integration).

import "./AdminShared.css";
import "./ApiDocs.css";

/* -------------------------------------------------------------------------- */
/*  Method badge                                                              */
/* -------------------------------------------------------------------------- */

function MethodBadge({ method }: { method: string }): JSX.Element {
  const cls = `docs-method docs-method--${method.toLowerCase()}`;
  return <span className={cls}>{method}</span>;
}

/* -------------------------------------------------------------------------- */
/*  Section: SCIM Provisioning                                                */
/* -------------------------------------------------------------------------- */

function ScimContent(): JSX.Element {
  return (
    <div className="docs-tab-panel">
      <div className="docs-section">
        <h2 className="docs-section__title">What is SCIM?</h2>
        <p>
          SCIM (System for Cross-domain Identity Management) is an open standard
          protocol (
          <a
            href="https://datatracker.ietf.org/doc/html/rfc7644"
            target="_blank"
            rel="noopener noreferrer"
          >
            RFC 7644
          </a>
          ) that enables automated user and group provisioning between identity
          providers and service providers. Our service implements SCIM v2
          endpoints so that your enterprise identity provider (e.g., Azure AD,
          Okta) can automatically push user and group membership changes.
        </p>
        <p>
          When group memberships change in your IdP, SCIM pushes those changes
          to our service. We then evaluate tenant rules to determine which Ring
          roles and location grants each user should receive, and sync those
          grants to the authorization backend (rAZNx).
        </p>
        <p>
          The SCIM API is the only endpoint Ring Enterprise exposes for
          programmatic integration. All other functionality is delivered
          through the browser-based administration UI.
        </p>
      </div>

      <div className="docs-section">
        <h2 className="docs-section__title">SCIM Endpoints</h2>
        <table className="docs-endpoint-table">
          <thead>
            <tr>
              <th style={{ width: "70px" }}>Method</th>
              <th>Path</th>
              <th style={{ width: "120px" }}>Auth</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td><MethodBadge method="GET" /></td>
              <td><code>/scim/v2/ServiceProviderConfig</code></td>
              <td>None</td>
              <td>Returns SCIM capabilities and supported features. Used by your IdP during initial configuration.</td>
            </tr>
            <tr>
              <td><MethodBadge method="GET" /></td>
              <td><code>/scim/v2/Schemas</code></td>
              <td>None</td>
              <td>Returns SCIM schema definitions for User and Group resources.</td>
            </tr>
            <tr>
              <td><MethodBadge method="POST" /></td>
              <td><code>/scim/v2/Users</code></td>
              <td>Bearer token</td>
              <td>Creates or provisions a user. Called by your IdP when a user is assigned to the SCIM application.</td>
            </tr>
            <tr>
              <td><MethodBadge method="PATCH" /></td>
              <td><code>/scim/v2/Groups/{"{id}"}</code></td>
              <td>Bearer token</td>
              <td>Updates group membership (add/remove members). Triggers grant reconciliation for affected users.</td>
            </tr>
          </tbody>
        </table>
      </div>

      <div className="docs-section">
        <h2 className="docs-section__title">Bearer Token Authentication</h2>
        <p>
          SCIM endpoints that modify data require a bearer token. You must
          include the token in the <code>Authorization</code> header:
        </p>
        <pre className="docs-codeblock"><code>{`Authorization: Bearer <scim-bearer-token>`}</code></pre>
        <p>
          The token is configured per-tenant in <code>alpha.yaml</code>. We
          perform SHA-256 hashed comparison (the raw token is never stored).
          Invalid or missing tokens return <code>401 Unauthorized</code>.
        </p>
      </div>

      <div className="docs-section">
        <h2 className="docs-section__title">Reconciliation Flow</h2>
        <p>
          When your IdP sends a SCIM group membership change, the following
          reconciliation pipeline executes:
        </p>
        <ol className="docs-steps">
          <li>
            <strong>Group update received</strong> &mdash;{" "}
            <code>ScimPatchHandler</code> processes the SCIM PATCH request and
            writes membership changes to the Memberships DynamoDB table.
          </li>
          <li>
            <strong>Rule evaluation</strong> &mdash;{" "}
            <code>RuleEvaluator</code> matches the user&rsquo;s group names
            against tenant rules defined in <code>alpha.yaml</code> to compute{" "}
            <code>DesiredGrant</code> objects (role + location pairs).
          </li>
          <li>
            <strong>Grant collapse</strong> &mdash;{" "}
            <code>DesiredStateComputer</code> deduplicates grants by (location,
            role). When conflicts exist, the higher privilege role wins.
          </li>
          <li>
            <strong>User provisioning</strong> &mdash;{" "}
            <code>ProvisioningManager.provisionUserIfNeeded()</code> creates the
            Ring identity (BlueBell + CloudAuth) if the user doesn&rsquo;t
            already have one.
          </li>
          <li>
            <strong>Grant sync</strong> &mdash; <code>RpacsSyncer</code> diffs
            the desired grants against current grants in rAZNx and applies
            additions / removals.
          </li>
        </ol>
      </div>

      <div className="docs-section">
        <h2 className="docs-section__title">Example: SCIM Group Membership Update</h2>
        <pre className="docs-codeblock"><code>{`curl -X PATCH \\
  "https://us-west-2-alpha.3zg6af5ltguxgim.create-burner.amazon.dev/scim/v2/Groups/wwos-fleet-admins" \\
  -H "Authorization: Bearer <scim-bearer-token>" \\
  -H "Content-Type: application/scim+json" \\
  -d '{
    "schemas": ["urn:ietf:params:scim:api:messages:2.0:PatchOp"],
    "Operations": [
      {
        "op": "add",
        "path": "members",
        "value": [{ "value": "johndoe", "display": "johndoe@amazon.com" }]
      }
    ]
  }'

# Response: 200 OK
{
  "schemas": ["urn:ietf:params:scim:schemas:core:2.0:Group"],
  "id": "wwos-fleet-admins",
  "displayName": "WWOS Fleet Admins",
  "members": [
    { "value": "johndoe", "display": "johndoe@amazon.com" }
  ]
}`}</code></pre>
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  Main page component                                                       */
/* -------------------------------------------------------------------------- */

export function ApiDocsPage(): JSX.Element {
  return (
    <section className="admin-page">
      <header className="page-header">
        <div>
          <h1 className="page-title">API Documentation</h1>
          <p className="page-subtitle">
            SCIM provisioning reference &mdash; the Ring Enterprise endpoint for
            programmatic integration with your identity provider.
          </p>
        </div>
      </header>

      <ScimContent />
    </section>
  );
}
